import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { decideSkillRequest, listSkillRequests } from "@/lib/api";
import { Empty, TH, TR, errMsg } from "@/components/accounts/shared";

// 1-1 Personal Training and Semi Private Training need a master admin's approval (Anish, 27 Oct 2026).
// When anyone else gives one of them in User Management, an invite or a hire, it waits here.
const SEL = "bg-[#0a0a0a] border border-[#262626] rounded-md h-8 px-2 text-xs text-zinc-200";
const STATUS = { requested: "Waiting", approved: "Approved", declined: "Declined" };

export default function SkillRequests({ onCount }) {
  const [rows, setRows] = useState(null);
  const [show, setShow] = useState("requested");
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const r = await listSkillRequests();
      setRows(r);
      onCount?.(r.filter((x) => x.status === "requested").length);
    } catch (e) { toast.error(errMsg(e, "Could not load")); setRows([]); }
  }, [onCount]);
  useEffect(() => { load(); }, [load]);

  const decide = async (r, approve) => {
    const note = approve ? "" : window.prompt(`Decline ${r.skill_label} for ${r.user_name || r.email}? Add a note (optional).`);
    if (note === null) return;
    setBusy(r.id);
    try {
      await decideSkillRequest(r.id, { approve, note: note || "" });
      toast.success(approve ? `${r.skill_label} added` : "Declined");
      load();
    } catch (e) { toast.error(errMsg(e, "Could not save")); } finally { setBusy(""); }
  };

  if (!rows) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const shown = rows.filter((r) => show === "all" || r.status === show);
  const waiting = rows.filter((r) => r.status === "requested").length;
  return (
    <div className="card-surface overflow-x-auto" data-testid="skill-requests">
      <div className="px-4 py-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-100">Skill approvals{waiting ? ` · ${waiting} waiting` : ""}</div>
          <div className="text-[11px] text-zinc-500 max-w-2xl">1-1 Personal Training and Semi Private Training need your approval. When anyone else gives one of them, it waits here. Approving adds the skill at that gym.</div>
        </div>
        <select value={show} onChange={(e) => setShow(e.target.value)} className={SEL} data-testid="skill-requests-status">
          <option value="requested">Waiting</option><option value="approved">Approved</option>
          <option value="declined">Declined</option><option value="all">All</option>
        </select>
      </div>
      {shown.length === 0 ? <Empty>No requests here.</Empty> : (
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className={TH}>
            <th className="px-3 py-2 font-medium">Requested</th><th className="px-3 py-2 font-medium">Gym</th>
            <th className="px-3 py-2 font-medium">Who</th><th className="px-3 py-2 font-medium">Skill</th>
            <th className="px-3 py-2 font-medium">Status</th><th />
          </tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className={TR} data-testid={`skill-request-${r.id}`}>
                <td className="px-3 py-2 text-xs text-zinc-400 whitespace-nowrap">{String(r.requested_at).slice(0, 10)}<div className="text-[10px] text-zinc-500">by {r.requested_by}</div></td>
                <td className="px-3 py-2 text-zinc-300">{r.gym || "—"}</td>
                <td className="px-3 py-2 text-xs text-zinc-200">{r.user_name || r.email}
                  {!r.user_id && <div className="text-[10px] text-amber-300">Not signed in to FPA yet</div>}</td>
                <td className="px-3 py-2 text-xs text-zinc-200">{r.skill_label}</td>
                <td className="px-3 py-2 text-xs text-zinc-400">{STATUS[r.status] || r.status}
                  {r.decided_by && <div className="text-[10px] text-zinc-500">{r.decided_by}{r.decision_note ? `: ${r.decision_note}` : ""}</div>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.status === "requested" && <>
                    <Button size="sm" variant="ghost" disabled={busy === r.id || !r.user_id} onClick={() => decide(r, true)} className="h-7 text-emerald-300" data-testid={`approve-skill-${r.id}`}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
                    <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => decide(r, false)} className="h-7 text-rose-300" data-testid={`decline-skill-${r.id}`}><X className="h-3.5 w-3.5 mr-1" />Decline</Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
